import { useEffect, useState, type ReactNode } from 'react'

import { api } from '../api'
import { datum, heuteIso } from '../format'
import { useAktion, useLaden } from '../hooks'
import ArtikelAuswahl from './ArtikelAuswahl'
import Meldung from './Meldung'
import type { Vermerk, Vermerkart } from '../typen'

const ARTEN: { wert: Vermerkart; titel: string }[] = [
  { wert: 'layout', titel: 'Layout' },
  { wert: 'kontrakt', titel: 'Kontrakt' },
  { wert: 'test', titel: 'Test' },
  { wert: 'frist', titel: 'Frist' },
  { wert: 'lieferung', titel: 'Lieferung' },
  { wert: 'sonstiges', titel: 'Sonstiges' },
]

interface PflegeEigenschaften {
  titel: string
  /** Was in der Kopfzeile steht, wenn nichts da ist: „nichts im Horizont“. */
  leer: string
  anzahl: number
  /** Der Satz, der erklärt, wofür der Abschnitt da ist - erst sichtbar, wenn das Formular offen ist. */
  erklaerung: ReactNode
  formular: ReactNode
  children?: ReactNode
}

/**
 * Ein Pflegeabschnitt der Disposition: eine Zeile mit Titel und Stand („Geplante Produktion: nichts
 * im Horizont“), daneben „Eintragen“, das das Formular aufklappt. Mit Daten steht die Tabelle
 * darunter, das Formular bleibt hinter dem Knopf. So nimmt ein leerer Abschnitt eine Zeile ein
 * statt eines ganzen Formulars.
 */
export function Pflegeabschnitt({ titel, leer, anzahl, erklaerung, formular, children }: PflegeEigenschaften) {
  const [offen, setOffen] = useState(false)
  return (
    <div className="karte pflege">
      <div className="leiste pflege-kopf">
        <b>{titel}</b>
        {anzahl === 0 ? <span className="hinweis">{leer}</span> : <span className="hinweis">{anzahl} {anzahl === 1 ? 'Eintrag' : 'Einträge'}</span>}
        <button onClick={() => setOffen((o) => !o)}>{offen ? 'Schließen' : 'Eintragen'}</button>
      </div>
      {offen && (
        <div className="pflege-formular">
          <p className="hinweis">{erklaerung}</p>
          {formular}
        </div>
      )}
      {anzahl > 0 && children}
    </div>
  )
}

interface Eigenschaften {
  artikel: { artikelnummer: string; bezeichnung: string }[]
  onGeaendert?: () => void
}

/** Vermerke anlegen und ansehen - fuer die Artikel, die gerade auf dem Blatt stehen. */
export default function Vermerke({ artikel, onGeaendert }: Eigenschaften) {
  const [nummer, setNummer] = useState(artikel[0]?.artikelnummer ?? '')
  const [art, setArt] = useState<Vermerkart>('sonstiges')
  const [text, setText] = useState('')
  const [tag, setTag] = useState('')
  const [bis, setBis] = useState('')
  const [faellig, setFaellig] = useState('')
  const { fehler, setFehler, ausfuehren } = useAktion()
  const nummern = artikel.map((a) => a.artikelnummer).join('|')
  const heute = heuteIso()

  const { daten, fehler: ladeFehler, neuLaden: laden } = useLaden(async () => {
    const gesucht = new Set(nummern.split('|'))
    const werte = await api.vermerke()
    return werte.filter((v) => v.artikelnummer !== null && gesucht.has(v.artikelnummer))
  }, [nummern])
  const liste = daten ?? []

  useEffect(() => {
    if (!nummer && artikel.length > 0) setNummer(artikel[0].artikelnummer)
  }, [artikel, nummer])

  function speichern() {
    if (!nummer || !text.trim()) {
      setFehler('Artikel und Text angeben.')
      return
    }
    void ausfuehren(async () => {
      await api.vermerkAnlegen({ artikel: nummer, art, text, tag: tag || null, bis: bis || null, faellig: faellig || null })
      setText('')
      setTag('')
      setBis('')
      setFaellig('')
      laden()
      onGeaendert?.()
    })
  }

  function erledigt(v: Vermerk) {
    void ausfuehren(async () => {
      await api.vermerkErledigt(v.id, v.erledigt_am === null)
      laden()
      onGeaendert?.()
    })
  }

  function loeschen(id: number) {
    void ausfuehren(async () => {
      await api.vermerkLoeschen(id)
      laden()
      onGeaendert?.()
    })
  }

  const formular = (
    <>
      <div className="leiste felder">
        <label className="feld">
          <span className="hinweis">Material</span>
          <ArtikelAuswahl artikel={artikel} wert={nummer} onChange={setNummer} />
        </label>
        <label className="feld">
          <span className="hinweis">Art</span>
          <select value={art} onChange={(e) => setArt(e.target.value as Vermerkart)}>
            {ARTEN.map((a) => (
              <option key={a.wert} value={a.wert}>
                {a.titel}
              </option>
            ))}
          </select>
        </label>
        <label className="feld" title="Leer: der Vermerk gilt für den Artikel, nicht für einen Tag">
          <span className="hinweis">Tag (leer = gilt für den Artikel)</span>
          <input type="date" value={tag} onChange={(e) => setTag(e.target.value)} />
        </label>
        <label className="feld">
          <span className="hinweis">bis (optional)</span>
          <input type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
        </label>
        <label
          className="feld"
          title="Mit Fälligkeit wird der Vermerk zur Aufgabe: Auslagerungstest, Layoutfreigabe, Kontraktende. Er steht in der Übersicht und auf der Seite Fällig, bis er abgehakt ist."
        >
          <span className="hinweis">fällig am (macht ihn zur Aufgabe)</span>
          <input type="date" value={faellig} onChange={(e) => setFaellig(e.target.value)} />
        </label>
      </div>
      <div className="leiste felder">
        <label className="feld" style={{ flex: 1, minWidth: 240 }}>
          <span className="hinweis">Text</span>
          <input
            type="text"
            placeholder="z.B. erstmals mit neuem Layout geliefert"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <button className="haupt" onClick={speichern}>
          Vermerken
        </button>
      </div>
      {fehler && <Meldung art="fehler">{fehler}</Meldung>}
    </>
  )

  return (
    <>
    <Pflegeabschnitt
      titel="Vermerke"
      leer="keine"
      anzahl={liste.length}
      erklaerung="Die gelben Zellen der Kladde: „erstmals mit neuem Layout“, „nur für Rewe“, „Kontrakt bis Dezember“. Erscheinen in der Tabelle oben und im Excel-Export."
      formular={formular}
    >
      <table>
        <tbody>
          {liste.map((v) => (
            <tr key={v.id}>
              <td>{v.artikelnummer}</td>
              <td className="vermerk-zelle">
                <b>{v.art}</b>: {v.text}
              </td>
              <td className="hinweis">
                {v.tag ? datum(v.tag) : 'ohne Tag'}
                {v.bis ? ` - ${datum(v.bis)}` : ''}
              </td>
              <td className={v.faellig && !v.erledigt_am && v.faellig < heute ? 'unter-mindestbestand' : 'hinweis'}>
                {v.faellig && (v.erledigt_am ? `erledigt ${datum(v.erledigt_am)}` : `fällig ${datum(v.faellig)}`)}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {v.faellig && (
                  <button onClick={() => erledigt(v)} style={{ marginRight: 6 }}>
                    {v.erledigt_am ? 'Wieder öffnen' : 'Erledigt'}
                  </button>
                )}
                <button onClick={() => loeschen(v.id)}>Entfernen</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Pflegeabschnitt>
    {ladeFehler && <Meldung art="fehler" titel="Vermerke konnten nicht geladen werden">{ladeFehler}</Meldung>}
    </>
  )
}
